import React, { useState, useEffect } from 'react';
import {
  Plus,
  Trash2,
  Edit3,
  Save,
  X,
  Calendar,
  Clock,
  User,
  Target,
  Flag,
  CheckSquare,
  Tag,
  Settings,
  Copy,
  FileText,
  Briefcase,
  AlertCircle,
  FolderOpen,
  Users,
  Repeat
} from 'lucide-react';
import api from '../utils/api';
import RecurringTaskModal from './RecurringTaskModal';
import InputWithVariablePicker from './InputWithVariablePicker';
import SubtaskTemplateModal from './SubtaskTemplateModal';
import TaskTemplateSelector from './TaskTemplateSelector';
import { formatEstimatedEffort } from '../utils/effortFormatting';

const ProjectTaskTemplatesSettings = ({ user }) => {
  const [projectTemplates, setProjectTemplates] = useState([]);
  const [taskTemplates, setTaskTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('projects');
  const [teamMembers, setTeamMembers] = useState([]);
  const [defaultStages, setDefaultStages] = useState([]);
  const [taskCategories, setTaskCategories] = useState([]);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [showSubtaskModal, setShowSubtaskModal] = useState(false);
  const [editingSubtaskIndex, setEditingSubtaskIndex] = useState(null);
  const [showTaskSelector, setShowTaskSelector] = useState(false);
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [recurringDescription, setRecurringDescription] = useState('');

  // Project template form state
  const [projectTemplate, setProjectTemplate] = useState({
    name: '',
    description: '',
    category: 'General',
    stages: [
      { name: 'Planning', color: '#6B7280', order: 1 },
      { name: 'In Progress', color: '#3B82F6', order: 2 },
      { name: 'Review', color: '#F59E0B', order: 3 },
      { name: 'Complete', color: '#10B981', order: 4 }
    ],
    default_tasks: [],
    estimated_duration: '',
    estimated_duration_metric: 'days', // minutes, hours, days
    complexity: 'Medium',
    status: 'open',
    due_date_offset: '',
    team_members: []
  });

  // Task template form state
  const [taskTemplate, setTaskTemplate] = useState({
    name: '',
    description: '',
    category: 'General',
    priority: 'Normal',
    estimated_effort_value: '',
    estimated_effort_unit: 'hours', // minutes, hours, days
    recurring_rule: '',
    default_stage: 'To Do',
    due_date_offset: '',
    due_time: '',
    start_date_offset: '',
    assignees: [],
    is_milestone: false,
    checklist_items: [],
    tags: [],
    subtasks: [],  // Array of subtask definitions
    comments: []   // Array of comment text strings
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const [projectsRes, tasksRes, teamMembersRes, defaultStagesRes, categoriesRes] = await Promise.all([
        api.get('/templates/projects'),
        api.get('/templates/tasks'),
        api.get('/team'),
        api.get('/settings/default-stages'),
        api.get('/task-categories')
      ]);
      
      setProjectTemplates(projectsRes.data);
      setTaskTemplates(tasksRes.data);
      
      // Handle different API response structures for team members
      let members = [];
      if (Array.isArray(teamMembersRes.data)) {
        members = teamMembersRes.data;
      } else if (teamMembersRes.data?.members) {
        members = teamMembersRes.data.members;
      } else if (teamMembersRes.data?.team_members) {
        members = teamMembersRes.data.team_members;
      }
      setTeamMembers(members);
      
      // Store default stages
      setDefaultStages(defaultStagesRes.data || []);
      
      // Store task categories
      setTaskCategories(categoriesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProjectTemplate = async () => {
    try {
      const url = editingProject 
        ? `/templates/projects/${editingProject.id}`
        : '/templates/projects';
      
      const method = editingProject ? 'put' : 'post';
      
      await api[method](url, projectTemplate);
      
      setShowProjectModal(false);
      setEditingProject(null);
      resetProjectForm();
      fetchTemplates();
    } catch (error) {
      console.error('Failed to save project template:', error);
      alert('Failed to save project template');
    }
  };

  const handleSaveTaskTemplate = async () => {
    try {
      const url = editingTask 
        ? `/templates/tasks/${editingTask.id}`
        : '/templates/tasks';
      
      const method = editingTask ? 'put' : 'post';
      
      await api[method](url, taskTemplate);
      
      setShowTaskModal(false);
      setEditingTask(null);
      resetTaskForm();
      fetchTemplates();
    } catch (error) {
      console.error('Failed to save task template:', error);
      alert('Failed to save task template');
    }
  };

  const handleDeleteProjectTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this project template?')) return;
    
    try {
      await api.delete(`/templates/projects/${templateId}`);
      fetchTemplates();
    } catch (error) {
      console.error('Failed to delete project template:', error);
      alert('Failed to delete project template');
    }
  };

  const handleDeleteTaskTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this task template?')) return;
    
    try {
      await api.delete(`/templates/tasks/${templateId}`);
      fetchTemplates();
    } catch (error) {
      console.error('Failed to delete task template:', error);
      alert('Failed to delete task template');
    }
  };

  const handleEditProject = (template) => {
    setEditingProject(template);
    setProjectTemplate(template);
    setShowProjectModal(true);
  };

  const handleEditTask = (template) => {
    setEditingTask(template);
    setTaskTemplate(template);
    setShowTaskModal(true);
  };

  const handleDuplicateProject = (template) => {
    setProjectTemplate({
      ...template,
      name: `${template.name} (Copy)`,
      id: undefined
    });
    setEditingProject(null);
    setShowProjectModal(true);
  };

  const handleDuplicateTask = (template) => {
    setTaskTemplate({
      ...template,
      name: `${template.name} (Copy)`,
      id: undefined
    });
    setEditingTask(null);
    setShowTaskModal(true);
  };

  const resetProjectForm = () => {
    // Use default stages from company settings, or fallback to hardcoded defaults
    const stagesToUse = defaultStages.length > 0 
      ? defaultStages.map((stage, index) => ({
          name: stage.name,
          color: stage.color,
          order: stage.order || index + 1
        }))
      : [
          { name: 'Planning', color: '#6B7280', order: 1 },
          { name: 'In Progress', color: '#3B82F6', order: 2 },
          { name: 'Review', color: '#F59E0B', order: 3 },
          { name: 'Complete', color: '#10B981', order: 4 }
        ];
    
    setProjectTemplate({
      name: '',
      description: '',
      category: 'General',
      stages: stagesToUse,
      default_tasks: [],
      estimated_duration: '',
      complexity: 'Medium',
      status: 'open',
      due_date_offset: '',
      team_members: []
    });
  };

  const resetTaskForm = () => {
    setTaskTemplate({
      name: '',
      description: '',
      category: 'General',
      priority: 'Normal',
      estimated_effort_value: '',
      estimated_effort_unit: 'hours',
      recurring_rule: '',
      default_stage: 'To Do',
      due_date_offset: '',
      due_time: '',
      start_date_offset: '',
      assignees: [],
      is_milestone: false,
      checklist_items: [],
      tags: [],
      subtasks: [],
      comments: []
    });
  };

  const addStage = () => {
    const newStage = {
      name: '',
      color: '#6B7280',
      order: projectTemplate.stages.length + 1
    };
    setProjectTemplate({
      ...projectTemplate,
      stages: [...projectTemplate.stages, newStage]
    });
  };

  const removeStage = (index) => {
    const updatedStages = projectTemplate.stages.filter((_, i) => i !== index);
    setProjectTemplate({
      ...projectTemplate,
      stages: updatedStages.map((stage, i) => ({ ...stage, order: i + 1 }))
    });
  };

  const updateStage = (index, field, value) => {
    const updatedStages = projectTemplate.stages.map((stage, i) => 
      i === index ? { ...stage, [field]: value } : stage
    );
    setProjectTemplate({
      ...projectTemplate,
      stages: updatedStages
    });
  };

  const addChecklistItem = () => {
    setTaskTemplate({
      ...taskTemplate,
      checklist_items: [...taskTemplate.checklist_items, { text: '', completed: false }]
    });
  };

  const removeChecklistItem = (index) => {
    const updatedItems = taskTemplate.checklist_items.filter((_, i) => i !== index);
    setTaskTemplate({
      ...taskTemplate,
      checklist_items: updatedItems
    });
  };

  const updateChecklistItem = (index, field, value) => {
    const updatedItems = taskTemplate.checklist_items.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    );
    setTaskTemplate({
      ...taskTemplate,
      checklist_items: updatedItems
    });
  };

  // Subtask management
  const handleAddSubtask = () => {
    setEditingSubtaskIndex(null);
    setShowSubtaskModal(true);
  };

  const handleEditSubtask = (index) => {
    setEditingSubtaskIndex(index);
    setShowSubtaskModal(true);
  };

  const handleSaveSubtask = (subtaskData) => {
    if (editingSubtaskIndex !== null) {
      // Update existing subtask
      const updatedSubtasks = taskTemplate.subtasks.map((subtask, i) => 
        i === editingSubtaskIndex ? subtaskData : subtask
      );
      setTaskTemplate({
        ...taskTemplate,
        subtasks: updatedSubtasks
      });
    } else {
      // Add new subtask
      setTaskTemplate({
        ...taskTemplate,
        subtasks: [...taskTemplate.subtasks, subtaskData]
      });
    }
    setShowSubtaskModal(false);
    setEditingSubtaskIndex(null);
  };

  const handleRemoveSubtask = (index) => {
    const updatedSubtasks = taskTemplate.subtasks.filter((_, i) => i !== index);
    setTaskTemplate({
      ...taskTemplate,
      subtasks: updatedSubtasks
    });
  };

  // Comment management
  const addComment = () => {
    setTaskTemplate({
      ...taskTemplate,
      comments: [...taskTemplate.comments, '']
    });
  };

  const removeComment = (index) => {
    const updatedComments = taskTemplate.comments.filter((_, i) => i !== index);
    setTaskTemplate({
      ...taskTemplate,
      comments: updatedComments
    });
  };

  const updateComment = (index, value) => {
    const updatedComments = taskTemplate.comments.map((comment, i) => 
      i === index ? value : comment
    );
    setTaskTemplate({
      ...taskTemplate,
      comments: updatedComments
    });
  };

  const handleRecurringSave = (cronExpression, description) => {
    setTaskTemplate({
      ...taskTemplate,
      recurring_rule: cronExpression
    });
    setRecurringDescription(description);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        <span className="ml-3 text-gray-600">Loading templates...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Project & Task Templates</h2>
          <p className="text-sm text-gray-600 mt-1">
            Create reusable templates to streamline project and task creation
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('projects')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'projects'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FolderOpen className="w-4 h-4 inline mr-2" />
            Project Templates ({projectTemplates.length})
          </button>
          <button
            onClick={() => setActiveTab('tasks')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'tasks'
                ? 'border-purple-500 text-purple-600'  
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <CheckSquare className="w-4 h-4 inline mr-2" />
            Task Templates ({taskTemplates.length})
          </button>
        </nav>
      </div>

      {/* Project Templates Tab */}
      {activeTab === 'projects' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Project Templates</h3>
            <button
              onClick={() => {
                resetProjectForm();
                setEditingProject(null);
                setShowProjectModal(true);
              }}
              className="flex items-center px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-md"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Project Template
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projectTemplates.map((template) => (
              <div key={template.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900">{template.name}</h4>
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => handleDuplicateProject(template)}
                      className="p-1 text-gray-400 hover:text-blue-600 rounded"
                      title="Duplicate Template"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleEditProject(template)}
                      className="p-1 text-gray-400 hover:text-blue-600 rounded"  
                      title="Edit Template"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteProjectTemplate(template.id)}
                      className="p-1 text-gray-400 hover:text-red-600 rounded"
                      title="Delete Template"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-3">{template.description}</p>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>{template.category}</span>
                  <span>{template.stages?.length || 0} stages</span>
                </div>
              </div>
            ))}

            {projectTemplates.length === 0 && (
              <div className="col-span-full text-center py-8 text-gray-500">
                <FolderOpen className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No project templates yet</p>
                <p className="text-sm">Create your first template to get started</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Task Templates Tab */}
      {activeTab === 'tasks' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Task Templates</h3>
            <button
              onClick={() => {
                resetTaskForm();
                setEditingTask(null);
                setShowTaskModal(true);
              }}
              className="flex items-center px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-md"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Task Template
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {taskTemplates.map((template) => (
              <div key={template.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900">{template.name}</h4>
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => handleDuplicateTask(template)}
                      className="p-1 text-gray-400 hover:text-blue-600 rounded"
                      title="Duplicate Template"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleEditTask(template)}
                      className="p-1 text-gray-400 hover:text-blue-600 rounded"
                      title="Edit Template"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteTaskTemplate(template.id)}
                      className="p-1 text-gray-400 hover:text-red-600 rounded"
                      title="Delete Template"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-3">{template.description}</p>
                <div className="flex items-center justify-between text-xs">
                  <span className={`px-2 py-1 rounded-full ${
                    template.priority === 'High' ? 'bg-red-100 text-red-800' :
                    template.priority === 'Normal' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {template.priority}
                  </span>
                  <div className="flex items-center space-x-2 text-gray-500">
                    {(template.subtasks?.length > 0 || template.comments?.length > 0) && (
                      <div className="flex items-center space-x-2">
                        {template.subtasks?.length > 0 && (
                          <span title={`${template.subtasks.length} subtasks`}>
                            <CheckSquare className="w-3 h-3 inline mr-1" />
                            {template.subtasks.length}
                          </span>
                        )}
                        {template.comments?.length > 0 && (
                          <span title={`${template.comments.length} comments`}>
                            <FileText className="w-3 h-3 inline mr-1" />
                            {template.comments.length}
                          </span>
                        )}
                      </div>
                    )}
                    <span>{template.category}</span>
                  </div>
                </div>
              </div>
            ))}

            {taskTemplates.length === 0 && (
              <div className="col-span-full text-center py-8 text-gray-500">
                <CheckSquare className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No task templates yet</p>
                <p className="text-sm">Create your first template to get started</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Project Template Modal */}
      {showProjectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingProject ? 'Edit Project Template' : 'New Project Template'}
              </h3>
              <button
                onClick={() => {
                  setShowProjectModal(false);
                  setEditingProject(null);
                  resetProjectForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Template Name *
                  </label>
                  <InputWithVariablePicker
                    value={projectTemplate.name}
                    onChange={(e) => setProjectTemplate({...projectTemplate, name: e.target.value})}
                    placeholder="e.g., {{client.name}} - Website Redesign"
                    showVariablePicker={true}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Use variables like {`{{client.name}}`} for dynamic project names
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={projectTemplate.category}
                    onChange={(e) => setProjectTemplate({...projectTemplate, category: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    {taskCategories.length === 0 ? (
                      <>
                        <option value="General">General</option>
                        <option value="Development">Development</option>
                        <option value="Design">Design</option>
                        <option value="Marketing">Marketing</option>
                        <option value="Research">Research</option>
                        <option value="Client Work">Client Work</option>
                        <option value="Internal">Internal</option>
                      </>
                    ) : (
                      taskCategories.map((category) => (
                        <option key={category.id} value={category.name}>
                          {category.name}
                        </option>
                      ))
                    )}
                  </select>
                  {taskCategories.length === 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      Configure custom categories in Settings → Task Categories
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <InputWithVariablePicker
                  type="textarea"
                  value={projectTemplate.description}
                  onChange={(e) => setProjectTemplate({...projectTemplate, description: e.target.value})}
                  placeholder="Describe when to use this template... Use variable picker for dynamic content like organization name, client info, etc."
                  rows={3}
                  showVariablePicker={true}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Use variable picker to insert dynamic fields like organization name, client info, contact details, etc.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Default Status</label>
                  <select
                    value={projectTemplate.status}
                    onChange={(e) => setProjectTemplate({...projectTemplate, status: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="open">Open</option>
                    <option value="in-progress">In Progress</option>
                    <option value="on-hold">On Hold</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Complexity</label>
                  <select
                    value={projectTemplate.complexity}
                    onChange={(e) => setProjectTemplate({...projectTemplate, complexity: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="Simple">Simple</option>
                    <option value="Medium">Medium</option>
                    <option value="Complex">Complex</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Duration</label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={projectTemplate.estimated_duration}
                      onChange={(e) => setProjectTemplate({...projectTemplate, estimated_duration: e.target.value})}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="0"
                    />
                    <select
                      value={projectTemplate.estimated_duration_metric || 'days'}
                      onChange={(e) => setProjectTemplate({...projectTemplate, estimated_duration_metric: e.target.value})}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="minutes">Minutes</option>
                      <option value="hours">Hours</option>
                      <option value="days">Days</option>
                    </select>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Due Date Offset</label>
                <select
                  value={projectTemplate.due_date_offset || ''}
                  onChange={(e) => setProjectTemplate({...projectTemplate, due_date_offset: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">No default due date</option>
                  <option value="+1 day">1 day after start</option>
                  <option value="+3 days">3 days after start</option>
                  <option value="+1 week">1 week after start</option>
                  <option value="+2 weeks">2 weeks after start</option>
                  <option value="+1 month">1 month after start</option>
                  <option value="+2 months">2 months after start</option>
                  <option value="+3 months">3 months after start</option>
                  <option value="+6 months">6 months after start</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">Due date will be calculated when project is created</p>
              </div>

              {/* Linked Task Templates */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Linked Task Templates
                    <span className="text-xs text-gray-500 block">Tasks that will be automatically created with this project</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowTaskSelector(true)}
                    className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                  >
                    {projectTemplate.default_tasks.length > 0 ? 'Edit Selection' : '+ Select Tasks'}
                  </button>
                </div>
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  {projectTemplate.default_tasks.length === 0 ? (
                    <div className="text-center py-6">
                      <CheckSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-sm text-gray-500 mb-3">
                        No task templates linked yet
                      </p>
                      <button
                        type="button"
                        onClick={() => setShowTaskSelector(true)}
                        className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700"
                      >
                        Select Task Templates
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {projectTemplate.default_tasks.map((taskId) => {
                        const task = taskTemplates.find(t => t.id === taskId);
                        if (!task) return null;
                        return (
                          <div key={taskId} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900">{task.name}</p>
                              <p className="text-xs text-gray-500">
                                {task.category} • {task.priority} priority
                                {task.estimated_effort_value && ` • ${formatEstimatedEffort(task.estimated_effort_value, task.estimated_effort_unit)}`}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                setProjectTemplate({
                                  ...projectTemplate,
                                  default_tasks: projectTemplate.default_tasks.filter(id => id !== taskId)
                                });
                              }}
                              className="ml-3 p-1 text-red-600 hover:bg-red-50 rounded"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        );
                      })}
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-xs text-gray-600">
                          <CheckSquare className="w-3 h-3 inline mr-1" />
                          {projectTemplate.default_tasks.length} task template{projectTemplate.default_tasks.length !== 1 ? 's' : ''} will be automatically created
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">Project Stages</label>
                  <button
                    onClick={addStage}
                    className="text-sm text-purple-600 hover:text-purple-700"
                  >
                    + Add Stage
                  </button>
                </div>
                <div className="space-y-2">
                  {projectTemplate.stages.map((stage, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={stage.name}
                        onChange={(e) => updateStage(index, 'name', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="Stage name"
                      />
                      <input
                        type="color"
                        value={stage.color}
                        onChange={(e) => updateStage(index, 'color', e.target.value)}
                        className="w-12 h-10 border border-gray-300 rounded"
                      />
                      {projectTemplate.stages.length > 1 && (
                        <button
                          onClick={() => removeStage(index)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowProjectModal(false);
                  setEditingProject(null);
                  resetProjectForm();
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveProjectTemplate}
                className="flex items-center px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-md"
              >
                <Save className="w-4 h-4 mr-2" />
                {editingProject ? 'Update Template' : 'Create Template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Task Template Modal */}
      {showTaskModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingTask ? 'Edit Task Template' : 'New Task Template'}
              </h3>
              <button
                onClick={() => {
                  setShowTaskModal(false);
                  setEditingTask(null);
                  resetTaskForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic Information */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Basic Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Template Name *
                    </label>
                    <InputWithVariablePicker
                      value={taskTemplate.name}
                      onChange={(e) => setTaskTemplate({...taskTemplate, name: e.target.value})}
                      placeholder="e.g., Setup - Initial Meeting with {{client.name}}"
                      showVariablePicker={true}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Use variables like {`{{client.name}}`} for dynamic task titles
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Default Stage</label>
                    <select
                      value={taskTemplate.default_stage}
                      onChange={(e) => setTaskTemplate({...taskTemplate, default_stage: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="To Do">To Do</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Review">Review</option>
                      <option value="Done">Done</option>
                    </select>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <InputWithVariablePicker
                    type="textarea"
                    value={taskTemplate.description}
                    onChange={(e) => setTaskTemplate({...taskTemplate, description: e.target.value})}
                    placeholder="Describe what this task involves... Use variable picker for dynamic content"
                    rows={3}
                    showVariablePicker={true}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Use variable picker to insert dynamic fields like organization name, client info, contact details, etc.
                  </p>
                </div>
              </div>

              {/* Classification */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Classification</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <select
                      value={taskTemplate.category}
                      onChange={(e) => setTaskTemplate({...taskTemplate, category: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      {taskCategories.length === 0 ? (
                        <>
                          <option value="General">General</option>
                          <option value="Feature">Feature</option>
                          <option value="Bug">Bug</option>
                          <option value="Deliverable">Deliverable</option>
                          <option value="Meeting Prep">Meeting Prep</option>
                          <option value="Research">Research</option>
                          <option value="Documentation">Documentation</option>
                          <option value="Development">Development</option>
                          <option value="Design">Design</option>
                          <option value="Marketing">Marketing</option>
                          <option value="Testing">Testing</option>
                        </>
                      ) : (
                        taskCategories.map((category) => (
                          <option key={category.id} value={category.name}>
                            {category.name}
                          </option>
                        ))
                      )}
                    </select>
                    {taskCategories.length === 0 && (
                      <p className="text-xs text-gray-500 mt-1">
                        Configure custom categories in Settings → Task Categories
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                    <select
                      value={taskTemplate.priority}
                      onChange={(e) => setTaskTemplate({...taskTemplate, priority: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="Low">Low</option>
                      <option value="Normal">Normal</option>
                      <option value="High">High</option>
                      <option value="Critical">Critical</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Effort</label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={taskTemplate.estimated_effort_value}
                        onChange={(e) => setTaskTemplate({...taskTemplate, estimated_effort_value: e.target.value ? parseInt(e.target.value) : ''})}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="0"
                      />
                      <select
                        value={taskTemplate.estimated_effort_unit || 'hours'}
                        onChange={(e) => setTaskTemplate({...taskTemplate, estimated_effort_unit: e.target.value})}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        <option value="minutes">Minutes</option>
                        <option value="hours">Hours</option>
                        <option value="days">Days</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Scheduling */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Default Scheduling</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Date Offset</label>
                    <select
                      value={taskTemplate.start_date_offset}
                      onChange={(e) => setTaskTemplate({...taskTemplate, start_date_offset: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="">Start immediately</option>
                      <option value="0 days">Same day as project start</option>
                      <option value="+1 day">1 day after project start</option>
                      <option value="+2 days">2 days after project start</option>
                      <option value="+3 days">3 days after project start</option>
                      <option value="+1 week">1 week after project start</option>
                      <option value="+2 weeks">2 weeks after project start</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">When task should start relative to project start</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Due Date Offset</label>
                    <select
                      value={taskTemplate.due_date_offset}
                      onChange={(e) => setTaskTemplate({...taskTemplate, due_date_offset: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="">No due date</option>
                      <option value="+1 day">1 day after start</option>
                      <option value="+2 days">2 days after start</option>
                      <option value="+3 days">3 days after start</option>
                      <option value="+1 week">1 week after start</option>
                      <option value="+2 weeks">2 weeks after start</option>
                      <option value="+1 month">1 month after start</option>
                      <option value="+3 business days">3 business days after start</option>
                      <option value="+5 business days">5 business days after start</option>
                      <option value="+10 business days">10 business days after start</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Due date calculated from task start or project start</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Due Time</label>
                    <input
                      type="time"
                      value={taskTemplate.due_time}
                      onChange={(e) => setTaskTemplate({...taskTemplate, due_time: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">Time of day for due date (optional)</p>
                  </div>
                </div>
              </div>

              {/* Milestone & Recurring */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Task Properties</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="is_milestone"
                      checked={taskTemplate.is_milestone}
                      onChange={(e) => setTaskTemplate({...taskTemplate, is_milestone: e.target.checked})}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    />
                    <label htmlFor="is_milestone" className="text-sm font-medium text-gray-700">
                      Mark as milestone by default
                    </label>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Recurring Task</label>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            id="is_recurring_template"
                            checked={!!taskTemplate.recurring_rule}
                            onChange={(e) => {
                              if (!e.target.checked) {
                                setTaskTemplate({...taskTemplate, recurring_rule: ''});
                                setRecurringDescription('');
                              } else {
                                setShowRecurringModal(true);
                              }
                            }}
                            className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                          />
                          <label htmlFor="is_recurring_template" className="text-sm font-medium text-gray-700">
                            Recurring Template
                          </label>
                        </div>
                        
                        {taskTemplate.recurring_rule && (
                          <button
                            type="button"
                            onClick={() => setShowRecurringModal(true)}
                            className="flex items-center space-x-2 px-3 py-1 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all text-sm"
                          >
                            <Repeat className="w-3 h-3" />
                            <span>Edit Schedule</span>
                          </button>
                        )}
                      </div>

                      {taskTemplate.recurring_rule && recurringDescription && (
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                          <p className="text-sm text-purple-800">
                            <Repeat className="w-4 h-4 inline mr-1" />
                            {recurringDescription}
                          </p>
                        </div>
                      )}

                      {taskTemplate.recurring_rule && !recurringDescription && (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                          <p className="text-sm text-gray-600">
                            Advanced recurring schedule configured. Click "Edit Schedule" to modify.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Tags */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Tags</h4>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Default Tags</label>
                  <input
                    type="text"
                    value={taskTemplate.tags ? taskTemplate.tags.join(', ') : ''}
                    onChange={(e) => setTaskTemplate({
                      ...taskTemplate,
                      tags: e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag)
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="tag1, tag2, tag3"
                  />
                  <p className="text-xs text-gray-500 mt-1">Separate tags with commas</p>
                </div>
              </div>

              {/* Checklist Items */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-gray-900">Default Checklist Items</h4>
                  <button
                    onClick={addChecklistItem}
                    className="text-sm text-purple-600 hover:text-purple-700"
                  >
                    + Add Item
                  </button>
                </div>
                <div className="space-y-2">
                  {taskTemplate.checklist_items.map((item, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={item.text}
                        onChange={(e) => updateChecklistItem(index, 'text', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="Checklist item..."
                      />
                      <button
                        onClick={() => removeChecklistItem(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {taskTemplate.checklist_items.length === 0 && (
                    <p className="text-sm text-gray-500 italic">No checklist items yet. Click "Add Item" to get started.</p>
                  )}
                </div>
              </div>

              {/* Subtasks */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">Default Subtasks</h4>
                    <p className="text-xs text-gray-500 mt-1">Subtasks that will be automatically created when using this template</p>
                  </div>
                  <button
                    type="button"
                    onClick={handleAddSubtask}
                    className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                  >
                    + Add Subtask
                  </button>
                </div>
                <div className="space-y-2">
                  {taskTemplate.subtasks.map((subtask, index) => (
                    <div
                      key={index}
                      className="bg-white border border-gray-200 rounded-lg p-4 hover:border-purple-300 transition-colors cursor-pointer group"
                      onClick={() => handleEditSubtask(index)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <h5 className="text-sm font-medium text-gray-900 truncate">
                              {subtask.name || 'Untitled Subtask'}
                            </h5>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              subtask.priority === 'Critical' ? 'bg-red-100 text-red-700' :
                              subtask.priority === 'High' ? 'bg-orange-100 text-orange-700' :
                              subtask.priority === 'Normal' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {subtask.priority}
                            </span>
                          </div>
                          {subtask.description && (
                            <p className="text-sm text-gray-600 truncate mb-2">{subtask.description}</p>
                          )}
                          <div className="flex items-center space-x-3 text-xs text-gray-500">
                            {subtask.default_stage && (
                              <span>Stage: {subtask.default_stage}</span>
                            )}
                            {subtask.estimated_effort_value && (
                              <span>Effort: {subtask.estimated_effort_value} {subtask.estimated_effort_unit || 'hours'}</span>
                            )}
                            {subtask.category && (
                              <span>Category: {subtask.category}</span>
                            )}
                            {subtask.tags && subtask.tags.length > 0 && (
                              <span>{subtask.tags.length} tag{subtask.tags.length > 1 ? 's' : ''}</span>
                            )}
                            {subtask.checklist_items && subtask.checklist_items.length > 0 && (
                              <span>{subtask.checklist_items.length} checklist item{subtask.checklist_items.length > 1 ? 's' : ''}</span>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveSubtask(index);
                          }}
                          className="opacity-0 group-hover:opacity-100 p-2 text-red-600 hover:bg-red-50 rounded transition-opacity"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                  {taskTemplate.subtasks.length === 0 && (
                    <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                      <CheckSquare className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">No subtasks yet</p>
                      <p className="text-xs text-gray-400 mt-1">Click "Add Subtask" to create a template subtask</p>
                    </div>
                  )}
                </div>
              </div>

              {/* System Comments */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">System Comments</h4>
                    <p className="text-xs text-gray-500 mt-1">Comments that will be automatically added as system messages when task is created. Use variable picker to insert dynamic fields.</p>
                  </div>
                  <button
                    onClick={addComment}
                    className="text-sm text-purple-600 hover:text-purple-700"
                  >
                    + Add Comment
                  </button>
                </div>
                <div className="space-y-2">
                  {taskTemplate.comments.map((comment, index) => (
                    <div key={index} className="flex items-start space-x-2">
                      <div className="flex-1">
                        <InputWithVariablePicker
                          type="textarea"
                          value={comment}
                          onChange={(e) => updateComment(index, e.target.value)}
                          placeholder="System comment text... Use variable picker for dynamic content"
                          rows={2}
                          showVariablePicker={true}
                        />
                      </div>
                      <button
                        onClick={() => removeComment(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {taskTemplate.comments.length === 0 && (
                    <p className="text-sm text-gray-500 italic">No system comments yet. Click "Add Comment" to get started.</p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowTaskModal(false);
                  setEditingTask(null);
                  resetTaskForm();
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTaskTemplate}
                className="flex items-center px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-md"
              >
                <Save className="w-4 h-4 mr-2" />
                {editingTask ? 'Update Template' : 'Create Template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Advanced Recurring Task Modal */}
      <RecurringTaskModal
        isOpen={showRecurringModal}
        onClose={() => setShowRecurringModal(false)}
        onSave={handleRecurringSave}
        initialRule={taskTemplate.recurring_rule}
      />

      {/* Subtask Template Modal */}
      <SubtaskTemplateModal
        isOpen={showSubtaskModal}
        onClose={() => {
          setShowSubtaskModal(false);
          setEditingSubtaskIndex(null);
        }}
        onSave={handleSaveSubtask}
        subtask={editingSubtaskIndex !== null ? taskTemplate.subtasks[editingSubtaskIndex] : null}
        teamMembers={teamMembers}
      />

      {/* Task Template Selector Modal */}
      <TaskTemplateSelector
        isOpen={showTaskSelector}
        onClose={() => setShowTaskSelector(false)}
        taskTemplates={taskTemplates}
        selectedTaskIds={projectTemplate.default_tasks}
        onSave={(selectedIds) => {
          setProjectTemplate({
            ...projectTemplate,
            default_tasks: selectedIds
          });
        }}
      />
    </div>
  );
};

export default ProjectTaskTemplatesSettings;